/**
 * Manifest fixtures
 * -----------------
 * Demo-grade canonical data for the post-award workflow preview.
 *
 * Defines:
 *   - The 25-step workflow grouped into 6 phases (PHASES, STEPS)
 *   - Three seeded demo orders at different lifecycle stages so the
 *     stakeholder walkthrough shows breadth without scripted hand-waving
 *
 * Everything here is static; no network. Persisting user mutations is
 * the job of ManifestContext (localStorage).
 */

// Phase metadata. The 25 steps live below and reference these by id.
export const PHASES = [
  { id: 1, key: 'spec',       label: 'Spec & Source',     dayWindow: 'Day 0–2',    actor: 'Us → Factories' },
  { id: 2, key: 'quote',      label: 'Quote & Negotiate', dayWindow: 'Day 5–9',    actor: 'Us ↔ Factory' },
  { id: 3, key: 'contract',   label: 'Contract & Pay',    dayWindow: 'Day 9–14',   actor: 'Us ↔ Factory ↔ Freighter' },
  { id: 4, key: 'production', label: 'Production',        dayWindow: 'Day 14–45',  actor: 'Factory' },
  { id: 5, key: 'logistics',  label: 'Logistics',         dayWindow: 'Day 17–45+', actor: 'Freighter ↔ Customs ↔ Domestic' },
  { id: 6, key: 'invoice',    label: 'Invoice & Close',   dayWindow: 'Delivery →', actor: 'Us → Government' },
];

// Canonical 25-step list. `detail` is the rich-component key (or null for the
// generic step row). `targetDay` is the stakeholder-facing day target.
export const STEPS = [
  // Phase 1 — Spec & Source
  { id: 1,  phase: 1, title: 'Build technical specification sheet',         targetDay: 0,  owner: 'Us',         detail: 'tech_spec' },
  { id: 2,  phase: 1, title: 'Identify credible manufacturers',             targetDay: 0,  owner: 'Us',         detail: 'manufacturers' },
  { id: 3,  phase: 1, title: 'Reach out to manufacturers',                  targetDay: 2,  owner: 'Us',         detail: 'outreach' },

  // Phase 2 — Quote & Negotiate
  { id: 4,  phase: 2, title: 'Push from spec to quote',                     targetDay: 5,  owner: 'Us',         detail: null },
  { id: 5,  phase: 2, title: 'Receive quotes (3–6 expected)',               targetDay: 7,  owner: 'Factory',    detail: 'quotes' },
  { id: 6,  phase: 2, title: 'Negotiate price + terms',                     targetDay: 9,  owner: 'Us',         detail: null },

  // Phase 3 — Contract & Pay
  { id: 7,  phase: 3, title: 'Receive supplier contract + pro forma',       targetDay: 9,  owner: 'Factory',    detail: 'contract_docs' },
  { id: 8,  phase: 3, title: 'Get freight quote from our freighter',        targetDay: 10, owner: 'Freighter',  detail: 'freight_quote' },
  { id: 9,  phase: 3, title: 'Verify trade-assurance contract',             targetDay: 13, owner: 'Us',         detail: null },
  { id: 10, phase: 3, title: 'Pay trade-assurance / deposit',               targetDay: 14, owner: 'Us',         detail: null },
  { id: 11, phase: 3, title: 'Confirm payment received by factory',         targetDay: 14, owner: 'Factory',    detail: null },

  // Phase 4 — Production
  { id: 12, phase: 4, title: 'Ongoing production check-ins',                targetDay: 17, owner: 'Us',         detail: null },
  { id: 13, phase: 4, title: 'Production finished',                         targetDay: 35, owner: 'Factory',    detail: null },

  // Phase 5 — Logistics
  { id: 14, phase: 5, title: 'Send factory details to freighter',           targetDay: 17, owner: 'Us',         detail: null },
  { id: 15, phase: 5, title: 'Freighter coordinates pickup with factory',   targetDay: 35, owner: 'Freighter',  detail: null },
  { id: 16, phase: 5, title: 'Freighter issues tracking number',            targetDay: 36, owner: 'Freighter',  detail: 'tracking' },
  { id: 17, phase: 5, title: 'Customs clearance',                           targetDay: 44, owner: 'Customs',    detail: 'customs' },
  { id: 18, phase: 5, title: 'Receive customs release order',               targetDay: 44, owner: 'Customs',    detail: null },
  { id: 19, phase: 5, title: 'Forward release order to domestic shipper',   targetDay: 44, owner: 'Us',         detail: null },
  { id: 20, phase: 5, title: 'Domestic shipper picks up from warehouse',    targetDay: 45, owner: 'Domestic',   detail: null },
  { id: 21, phase: 5, title: 'Delivery to government + proof of delivery',  targetDay: 45, owner: 'Domestic',   detail: null },

  // Phase 6 — Invoice & Close
  { id: 22, phase: 6, title: 'Generate invoice from POD',                   targetDay: 45, owner: 'Us',         detail: null },
  { id: 23, phase: 6, title: 'Send invoice to government contact',          targetDay: 46, owner: 'Us',         detail: null },
  { id: 24, phase: 6, title: 'Confirm payment received in bank',            targetDay: 60, owner: 'Us',         detail: null },
  { id: 25, phase: 6, title: 'Close out — happy happy happy',               targetDay: 60, owner: 'Us',         detail: null },
];

export const TARGET_TOTAL_DAYS = 45;

// Helpers ------------------------------------------------------------

export const getPhase = (phaseId) => PHASES.find((p) => p.id === phaseId);
export const getStep  = (stepId)  => STEPS.find((s) => s.id === stepId);

export const stepsInPhase = (phaseId) => STEPS.filter((s) => s.phase === phaseId);

export const phaseProgress = (phase, stepStates) => {
  const inPhase = stepsInPhase(phase.id);
  const done = inPhase.filter((s) => stepStates[s.id]?.status === 'done').length;
  return { done, total: inPhase.length };
};

export const phaseStatus = (phase, stepStates) => {
  const { done, total } = phaseProgress(phase, stepStates);
  if (done === 0) return 'pending';
  if (done < total) {
    if (Object.values(stepStates).some((s) => s?.status === 'blocked' && getStep(s.stepId)?.phase === phase.id)) {
      return 'blocked';
    }
    return 'in_progress';
  }
  return 'done';
};

export const overallProgress = (stepStates) => {
  const total = STEPS.length;
  const done = STEPS.filter((s) => stepStates[s.id]?.status === 'done').length;
  return { done, total, pct: Math.round((done / total) * 100) };
};

export const findCurrentStep = (stepStates) => {
  // First step that's not 'done'
  for (const s of STEPS) {
    const state = stepStates[s.id]?.status || 'pending';
    if (state !== 'done') return s;
  }
  return STEPS[STEPS.length - 1];
};

// Seed orders --------------------------------------------------------

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
};

// Helper: build a stepStates object by marking a contiguous prefix as done.
const completeThrough = (lastStepId, extras = {}) => {
  const states = {};
  for (const s of STEPS) {
    if (s.id <= lastStepId) {
      states[s.id] = { stepId: s.id, status: 'done', notes: '', attachments: [], completedAt: daysAgo(40 - s.id) };
    } else {
      states[s.id] = { stepId: s.id, status: 'pending', notes: '', attachments: [] };
    }
  }
  // mark the very next step as in_progress so the "what's next" rail has a target
  if (states[lastStepId + 1]) states[lastStepId + 1].status = 'in_progress';
  return { ...states, ...extras };
};

// Demo factories used across orders (mirrors Phase 3 ProductSourcing shape)
const DEMO_FACTORIES_RADIOS = [
  { id: 'f1', name: 'Shenzhen Topsky Electronics Co.', country: 'China',   verified: true,  yearsActive: 12, moq: 50,  leadTimeDays: 28, contact: 'sales@topsky-radio.com',   listedPrice: 145, status: 'quoted' },
  { id: 'f2', name: 'Quanzhou Hytera Components',      country: 'China',   verified: true,  yearsActive: 9,  moq: 100, leadTimeDays: 35, contact: 'export@hytera-comp.cn',     listedPrice: 132, status: 'quoted' },
  { id: 'f3', name: 'Guangdong Maxon Radio Tech',      country: 'China',   verified: true,  yearsActive: 7,  moq: 50,  leadTimeDays: 30, contact: 'info@maxon-radio.com.cn',  listedPrice: 158, status: 'quoted' },
  { id: 'f4', name: 'Vertex Standard Industries',      country: 'Japan',   verified: true,  yearsActive: 22, moq: 25,  leadTimeDays: 21, contact: 'b2b@vertex-std.jp',         listedPrice: 215, status: 'quoted' },
  { id: 'f5', name: 'NorComm Two-Way Radio (CA)',      country: 'Canada',  verified: true,  yearsActive: 18, moq: 10,  leadTimeDays: 14, contact: 'orders@norcomm.ca',         listedPrice: 285, status: 'passed' },
  { id: 'f6', name: 'Anytone Tech Co.',                country: 'China',   verified: false, yearsActive: 5,  moq: 100, leadTimeDays: 32, contact: 'sales@anytone.net',         listedPrice: 119, status: 'contacted' },
  { id: 'f7', name: 'Yaesu Musen K.K.',                country: 'Japan',   verified: true,  yearsActive: 60, moq: 20,  leadTimeDays: 25, contact: 'export@yaesu.com',          listedPrice: 245, status: 'queued' },
];

const DEMO_QUOTES_RADIOS = [
  { factoryId: 'f1', unitPriceUsd: 138, leadTimeDays: 28, incoterm: 'FOB Shenzhen', certMatch: { ISED: true,  IP67: true,  MIL_STD_810: true  }, landedCostCadEach: 218, notes: 'Honored MOQ 50; 200pc bulk discount available.' },
  { factoryId: 'f2', unitPriceUsd: 124, leadTimeDays: 35, incoterm: 'EXW Quanzhou', certMatch: { ISED: true,  IP67: true,  MIL_STD_810: false }, landedCostCadEach: 209, notes: 'Cheapest unit price but missing MIL-STD-810 certification.' },
  { factoryId: 'f3', unitPriceUsd: 152, leadTimeDays: 30, incoterm: 'CIF Vancouver', certMatch: { ISED: true,  IP67: true,  MIL_STD_810: true  }, landedCostCadEach: 225, notes: 'CIF terms include marine insurance.' },
  { factoryId: 'f4', unitPriceUsd: 198, leadTimeDays: 21, incoterm: 'FOB Yokohama',  certMatch: { ISED: true,  IP67: true,  MIL_STD_810: true  }, landedCostCadEach: 286, notes: 'Premium build; matches RCMP-grade specs.' },
];

// Three seeded demo orders at different lifecycle stages
export const SEED_ORDERS = [
  {
    id: 'demo-radios',
    label: 'Tactical handheld radios — Phase 2',
    contractTitle: 'Tactical handheld two-way radios — RCMP J Division',
    buyer: 'Royal Canadian Mounted Police',
    solicitationNumber: 'M7594-25-1142',
    awardDate: daysAgo(7),
    contractValueCad: 287_500,
    quantity: 350,
    productSummary: 'IP67-rated handheld VHF/UHF two-way radios. ISED certified. 12-month warranty.',
    deliveryRegion: 'Regina, SK',
    deliveryDeadline: daysAgo(-38),
    factories: DEMO_FACTORIES_RADIOS,
    quotes: DEMO_QUOTES_RADIOS,
    pickedQuoteFactoryId: null,
    techSpec: {
      generated: true,
      lastUpdated: daysAgo(6),
      sections: [
        { heading: 'Frequency range',        body: 'VHF 136–174 MHz and UHF 400–470 MHz dual-band operation.' },
        { heading: 'Power output',           body: 'Adjustable 1W / 5W. Battery life ≥ 14h at 5/5/90 duty cycle.' },
        { heading: 'Environmental',          body: 'IP67 rated for dust and immersion. Operating range −30 °C to +60 °C.' },
        { heading: 'Certifications required', body: 'ISED RSS-119, IP67 (IEC 60529), MIL-STD-810H drop/shock.' },
        { heading: 'Battery + charging',     body: '2200 mAh Li-ion battery, single-unit drop-in charger included.' },
        { heading: 'Programming',            body: 'PC-side programming software + USB cable bundled with each unit.' },
        { heading: 'Warranty',               body: 'Minimum 12-month manufacturer warranty on radio and battery.' },
      ],
    },
    stepStates: completeThrough(4, {
      // Step 5 (quotes received) is current
      5: { stepId: 5, status: 'in_progress', notes: '4 of 5 quotes received. Yaesu Musen still outstanding — chase planned for Day 6.', attachments: [] },
    }),
    tracking: null,
    customs: null,
  },
  {
    id: 'demo-furniture',
    label: 'Modular office furniture — Phase 4 (Production)',
    contractTitle: 'Modular ergonomic workstations + task chairs — DND HQ refresh',
    buyer: 'Department of National Defence',
    solicitationNumber: 'W8475-24-9831',
    awardDate: daysAgo(22),
    contractValueCad: 412_000,
    quantity: 180,
    productSummary: 'Height-adjustable workstations (180) with matching ergonomic task chairs. BIFMA certified.',
    deliveryRegion: 'Ottawa, ON',
    deliveryDeadline: daysAgo(-25),
    factories: [
      { id: 'g1', name: 'Foshan ErgoMax Furniture',     country: 'China',  verified: true, yearsActive: 11, moq: 100, leadTimeDays: 32, contact: 'export@ergomax.cn',    listedPrice: 480, status: 'awarded' },
      { id: 'g2', name: 'Linak Industrial Lift Co.',    country: 'China',  verified: true, yearsActive: 8,  moq: 50,  leadTimeDays: 28, contact: 'sales@linak-cn.com',   listedPrice: 510, status: 'passed' },
      { id: 'g3', name: 'Steelcase Authorized (CA)',    country: 'Canada', verified: true, yearsActive: 30, moq: 1,   leadTimeDays: 18, contact: 'sales@steelcase.ca',   listedPrice: 920, status: 'passed' },
      { id: 'g4', name: 'Anji Comfort Chair Co.',       country: 'China',  verified: true, yearsActive: 14, moq: 50,  leadTimeDays: 25, contact: 'b2b@anji-comfort.com', listedPrice: 460, status: 'passed' },
    ],
    quotes: [
      { factoryId: 'g1', unitPriceUsd: 465, leadTimeDays: 32, incoterm: 'FOB Foshan',     certMatch: { BIFMA: true,  CAL117: true,  GREENGUARD: true },  landedCostCadEach: 730, notes: 'Selected — meets BIFMA and GREENGUARD; package deal incl. chairs.' },
      { factoryId: 'g2', unitPriceUsd: 498, leadTimeDays: 28, incoterm: 'EXW Linak',      certMatch: { BIFMA: true,  CAL117: false, GREENGUARD: true },  landedCostCadEach: 770, notes: 'Faster but missing CAL117.' },
      { factoryId: 'g4', unitPriceUsd: 442, leadTimeDays: 25, incoterm: 'FOB Ningbo',     certMatch: { BIFMA: false, CAL117: true,  GREENGUARD: false }, landedCostCadEach: 685, notes: 'Cheapest, but no BIFMA — disqualifying.' },
    ],
    pickedQuoteFactoryId: 'g1',
    techSpec: {
      generated: true,
      lastUpdated: daysAgo(21),
      sections: [
        { heading: 'Workstation frame',  body: 'Dual-motor electric height adjust 65–125 cm. 80 kg lift capacity.' },
        { heading: 'Worksurface',        body: '1600×800 mm thermofused laminate, 25 mm thickness, beveled edge.' },
        { heading: 'Task chair',         body: 'Mesh-back, lumbar adjustable, 4D armrests, BIFMA X5.1 certified.' },
        { heading: 'Certifications',     body: 'BIFMA, CAL117 fire safety, GREENGUARD Gold.' },
        { heading: 'Cable management',   body: 'Integrated tray + grommets at both ends of worksurface.' },
        { heading: 'Warranty',           body: '5-year on frame, 2-year on motor and electronics.' },
      ],
    },
    stepStates: completeThrough(11, {
      12: { stepId: 12, status: 'in_progress', notes: 'Day-22 production update from ErgoMax: 60% complete on workstations, chairs in QC. Pictures attached.', attachments: [{ name: 'production-update-day22.pdf', size: 3_400_000 }] },
    }),
    tracking: null,
    customs: null,
  },
  {
    id: 'demo-streetlights',
    label: 'LED streetlights — Phase 6 (Invoicing)',
    contractTitle: 'High-efficiency LED streetlights with smart controllers',
    buyer: 'Public Services and Procurement Canada (for City of Mississauga)',
    solicitationNumber: 'EN578-23-0227',
    awardDate: daysAgo(48),
    contractValueCad: 1_245_000,
    quantity: 1100,
    productSummary: '150W LED streetlight luminaires with NEMA 7-pin smart controllers, IP66 rated.',
    deliveryRegion: 'Mississauga, ON',
    deliveryDeadline: daysAgo(0),
    factories: [
      { id: 'l1', name: 'Hangzhou Brightway Lighting', country: 'China',  verified: true, yearsActive: 16, moq: 200, leadTimeDays: 30, contact: 'export@brightway-led.com', listedPrice: 95,  status: 'awarded' },
      { id: 'l2', name: 'Zhejiang Ledisson Co.',       country: 'China',  verified: true, yearsActive: 10, moq: 500, leadTimeDays: 35, contact: 'sales@ledisson.cn',         listedPrice: 89,  status: 'passed' },
      { id: 'l3', name: 'GE Current (CA distributor)', country: 'Canada', verified: true, yearsActive: 25, moq: 1,   leadTimeDays: 14, contact: 'orders@gecurrent.ca',       listedPrice: 162, status: 'passed' },
    ],
    quotes: [
      { factoryId: 'l1', unitPriceUsd: 92, leadTimeDays: 30, incoterm: 'FOB Ningbo',  certMatch: { DLC: true, CSA: true,  IP66: true }, landedCostCadEach: 138, notes: 'Selected — DLC + CSA listed product.' },
      { factoryId: 'l2', unitPriceUsd: 86, leadTimeDays: 35, incoterm: 'EXW',         certMatch: { DLC: true, CSA: false, IP66: true }, landedCostCadEach: 132, notes: 'No CSA — would block Canadian deployment.' },
    ],
    pickedQuoteFactoryId: 'l1',
    techSpec: {
      generated: true,
      lastUpdated: daysAgo(47),
      sections: [
        { heading: 'Luminaire',          body: '150W, 21,000 lm, 4000K CCT, ≥ 130 lm/W efficacy.' },
        { heading: 'Controller',         body: 'NEMA 7-pin photocontrol receptacle; dimmable 0–10V.' },
        { heading: 'Environmental',      body: 'IP66 ingress, IK10 impact, −40 °C to +50 °C operating.' },
        { heading: 'Certifications',     body: 'DLC Premium, CSA listed (Canadian deployment), FCC Part 15.' },
        { heading: 'Warranty',           body: '10-year on luminaire, 5-year on driver.' },
      ],
    },
    stepStates: completeThrough(21, {
      22: { stepId: 22, status: 'in_progress', notes: 'POD received from domestic shipper. Generating invoice today.', attachments: [{ name: 'proof-of-delivery.pdf', size: 1_200_000 }] },
    }),
    tracking: {
      carrier: 'OOCL Logistics + GTA Express (domestic)',
      trackingNumber: 'OOCL-7841-G2',
      waybillUrl: null,
      eta: daysAgo(-1),
      milestones: [
        { key: 'pickup',     label: 'Picked up from factory',  ts: daysAgo(15), done: true },
        { key: 'in_transit', label: 'In transit (ocean)',      ts: daysAgo(8),  done: true },
        { key: 'customs',    label: 'Customs hold',            ts: daysAgo(3),  done: true },
        { key: 'released',   label: 'Released',                ts: daysAgo(2),  done: true },
        { key: 'delivered',  label: 'Delivered to buyer',      ts: daysAgo(0),  done: true },
      ],
    },
    customs: {
      brokerName: 'Cole International',
      brokerContact: 'mhargrave@coleinternational.com',
      docs: {
        airWaybill:        { uploaded: true, name: 'awb-7841-G2.pdf' },
        certOfOrigin:      { uploaded: true, name: 'cert-origin-china-CN.pdf' },
        packingList:       { uploaded: true, name: 'packing-list-1100u.pdf' },
        commercialInvoice: { uploaded: true, name: 'commercial-invoice-USD.pdf' },
        releaseOrder:      { uploaded: true, name: 'release-order-CBSA.pdf' },
      },
    },
  },
];
